import { useEffect } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import {
    MapPin,
    Phone,
    Mail,
    ArrowRight,
    Building2,
    Scale,
    Check,
} from "lucide-react";
import { useSEO } from "@/lib/seo";
import { CITY_PAGES } from "@/lib/cities";
import { LANDING_PAGE_LIST } from "@/lib/landingPages";

export default function CityPage() {
    const { pathname } = useLocation();
    // Match /jurist-stockholm -> "stockholm"
    const slug = pathname.replace(/^\/jurist-/, "").replace(/\/$/, "");
    const city = CITY_PAGES[slug];

    useSEO({
        title: city?.seoTitle,
        description: city?.seoDescription,
    });

    useEffect(() => {
        if (!city) return;
        const id = `ld-city-${city.slug}`;
        const existing = document.getElementById(id);
        if (existing) existing.remove();

        const ld = {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "LegalService",
                    "@id": `https://bmjuridik.se/jurist-${city.slug}#legalservice`,
                    name: `BM Juridik — Jurist ${city.name}`,
                    description: city.seoDescription,
                    url: `https://bmjuridik.se/jurist-${city.slug}`,
                    telephone: "+46108085613",
                    email: "kontakt@bmjuridik.se",
                    image: "https://bmjuridik.se/logo/bm-juridik-monogram-256.png",
                    priceRange: "$$",
                    address: {
                        "@type": "PostalAddress",
                        streetAddress: city.address,
                        postalCode: city.postal.split(" ")[0],
                        addressLocality: city.addressLocality,
                        addressCountry: "SE",
                    },
                    geo: {
                        "@type": "GeoCoordinates",
                        latitude: city.latitude,
                        longitude: city.longitude,
                    },
                    areaServed: city.nearbyAreas.map((a) => ({
                        "@type": "City",
                        name: a,
                    })),
                    openingHoursSpecification: {
                        "@type": "OpeningHoursSpecification",
                        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
                        opens: "09:00",
                        closes: "17:00",
                    },
                },
                {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                        { "@type": "ListItem", position: 1, name: "Hem", item: "https://bmjuridik.se/" },
                        { "@type": "ListItem", position: 2, name: "Orter", item: "https://bmjuridik.se/orter" },
                        {
                            "@type": "ListItem",
                            position: 3,
                            name: `Jurist ${city.name}`,
                            item: `https://bmjuridik.se/jurist-${city.slug}`,
                        },
                    ],
                },
            ],
        };

        const script = document.createElement("script");
        script.id = id;
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);

        return () => {
            const el = document.getElementById(id);
            if (el) el.remove();
        };
    }, [city]);

    if (!city) return <Navigate to="/orter" replace />;

    return (
        <div data-testid={`city-page-${city.slug}`} className="bg-[#0A0A0B] text-[#F4F4F5]">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/5">
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.06]"
                    style={{
                        background:
                            "radial-gradient(circle at 25% 30%, #C4A977 0%, transparent 50%)",
                    }}
                />
                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C4A977]/10 border border-[#C4A977]/40 rounded-sm mb-7">
                        <MapPin size={12} className="text-[#C4A977]" />
                        <span className="text-[10px] tracking-[0.2em] uppercase text-[#C4A977] font-semibold">
                            Lokalt kontor · {city.name}
                        </span>
                    </div>
                    <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                        Jurist{" "}
                        <span className="italic text-[#C4A977]">i {city.nameLocative}.</span>
                    </h1>
                    <p className="mt-7 text-lg text-[#A1A1AA] leading-relaxed max-w-3xl">
                        {city.intro}
                    </p>

                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                        <a
                            href="#kontakt"
                            data-testid={`city-${city.slug}-cta`}
                            className="inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#C4A977] text-black hover:bg-[#DBC08F] rounded-sm text-sm tracking-wide font-medium group transition-colors"
                        >
                            Boka rådgivning
                            <ArrowRight
                                size={14}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </a>
                        <a
                            href="tel:+46108085613"
                            className="inline-flex items-center justify-center gap-3 px-7 py-4 border border-white/20 hover:border-[#C4A977] hover:text-[#C4A977] rounded-sm text-sm tracking-wide transition-colors"
                        >
                            <Phone size={14} />
                            010 - 808 56 13
                        </a>
                    </div>
                </div>
            </section>

            {/* OFFICE INFO */}
            <section
                id="kontakt"
                className="py-20 lg:py-24 border-b border-white/5"
            >
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
                    <div>
                        <p className="overline">Vårt kontor</p>
                        <h2 className="mt-4 font-serif font-light text-3xl lg:text-4xl tracking-tight leading-[1.1]">
                            BM Juridik {city.name}
                        </h2>
                        <div className="mt-8 space-y-5">
                            <div className="flex items-start gap-4">
                                <Building2
                                    size={18}
                                    className="text-[#C4A977] mt-1 flex-shrink-0"
                                />
                                <div>
                                    <p className="text-sm text-[#A1A1AA] mb-1">Adress</p>
                                    <p className="text-[#F4F4F5]">{city.address}</p>
                                    <p className="text-[#F4F4F5]">{city.postal}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Phone
                                    size={18}
                                    className="text-[#C4A977] mt-1 flex-shrink-0"
                                />
                                <div>
                                    <p className="text-sm text-[#A1A1AA] mb-1">Telefon</p>
                                    <a
                                        href="tel:+46108085613"
                                        className="text-[#F4F4F5] hover:text-[#C4A977]"
                                    >
                                        010 - 808 56 13
                                    </a>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Mail
                                    size={18}
                                    className="text-[#C4A977] mt-1 flex-shrink-0"
                                />
                                <div>
                                    <p className="text-sm text-[#A1A1AA] mb-1">E-post</p>
                                    <a
                                        href="mailto:kontakt@bmjuridik.se"
                                        className="text-[#F4F4F5] hover:text-[#C4A977]"
                                    >
                                        kontakt@bmjuridik.se
                                    </a>
                                </div>
                            </div>
                        </div>

                        <Link
                            to="/kontakt"
                            className="mt-10 inline-flex items-center gap-2 text-[#C4A977] hover:text-[#DBC08F] text-sm group"
                        >
                            Skicka kontaktformulär
                            <ArrowRight
                                size={14}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </Link>
                    </div>

                    <div>
                        <p className="overline">Varför oss</p>
                        <h2 className="mt-4 font-serif font-light text-3xl lg:text-4xl tracking-tight leading-[1.1]">
                            Lokal närvaro,{" "}
                            <span className="italic text-[#C4A977]">
                                rikstäckande styrka.
                            </span>
                        </h2>
                        <ul className="mt-8 space-y-4">
                            {city.usp.map((u) => (
                                <li
                                    key={u}
                                    className="flex items-start gap-3 text-[#F4F4F5]"
                                >
                                    <Check
                                        size={18}
                                        className="text-[#C4A977] mt-1 flex-shrink-0"
                                    />
                                    <span className="text-sm leading-relaxed">{u}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* COURTS + AREAS */}
            <section className="py-20 lg:py-24 border-b border-white/5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div>
                        <p className="overline">Vi företräder vid</p>
                        <h3 className="mt-4 font-serif text-2xl">
                            Domstolar i regionen
                        </h3>
                        <ul className="mt-6 space-y-2.5">
                            {city.courts.map((c) => (
                                <li
                                    key={c}
                                    className="flex items-center gap-3 text-sm text-[#F4F4F5]"
                                >
                                    <Scale
                                        size={14}
                                        className="text-[#C4A977] flex-shrink-0"
                                    />
                                    {c}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <p className="overline">Vi tar klienter från</p>
                        <h3 className="mt-4 font-serif text-2xl">
                            Hela {city.nameLocative}-regionen
                        </h3>
                        <div className="mt-6 flex flex-wrap gap-2">
                            {city.nearbyAreas.map((a) => (
                                <span
                                    key={a}
                                    className="px-3 py-1.5 bg-[#111113] border border-white/10 rounded-sm text-xs text-[#A1A1AA]"
                                >
                                    {a}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-20 lg:py-24 bg-[#0A0A0B] border-b border-white/5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <p className="overline">Tjänster i {city.nameLocative}</p>
                        <h2 className="mt-4 font-serif font-light text-3xl lg:text-4xl tracking-tight leading-[1.1]">
                            Allt vi gör —{" "}
                            <span className="italic text-[#C4A977]">i {city.nameLocative}.</span>
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {LANDING_PAGE_LIST.map((p) => (
                            <Link
                                key={p.slug}
                                to={`/${p.slug}`}
                                className="group bg-[#111113] border border-white/5 hover:border-[#C4A977]/40 px-5 py-4 rounded-sm flex items-center justify-between transition-colors"
                            >
                                <span className="text-sm text-[#F4F4F5] group-hover:text-[#C4A977]">
                                    {p.title}
                                </span>
                                <ArrowRight
                                    size={14}
                                    className="text-[#A1A1AA] group-hover:text-[#C4A977] group-hover:translate-x-1 transition-all"
                                />
                            </Link>
                        ))}
                    </div>
                    <div className="mt-10 text-center">
                        <Link
                            to="/tjanster"
                            className="inline-flex items-center gap-2 text-[#C4A977] hover:text-[#DBC08F] text-sm group"
                        >
                            Se alla rättsområden
                            <ArrowRight
                                size={14}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
